import { UserProfile } from "./compatibilityAI";
import { mapApiProfileToUserProfile } from "./mapApiProfile";
import { getCurrentUser, getUserById } from "../services/usersService";
import { tokenStorage } from "../services/api";
import {
  fetchQrScanHistory,
  fetchUnviewedQrScans,
  markQrScanViewed,
  recordQrScan,
  type QrScanApiItem,
} from "../services/socialService";

export interface ScanEvent {
  id: string;
  scannedUserId: string;
  scannerUserId: string;
  scannerProfile: UserProfile;
  timestamp: number;
  compatibility: number;
  viewed: boolean;
}

const PENDING_QR_SCAN_KEY = "pending_qr_scan_user_id";

function mapApiScan(item: QrScanApiItem): ScanEvent {
  return {
    id: item.id,
    scannedUserId: item.scannedUserId,
    scannerUserId: item.scannerUserId,
    scannerProfile: mapApiProfileToUserProfile(item.scannerProfile),
    timestamp: Date.parse(item.timestamp) || Date.now(),
    compatibility: item.compatibility,
    viewed: item.viewed,
  };
}

export function rememberPendingQrScan(scannedUserId: string): void {
  try {
    sessionStorage.setItem(PENDING_QR_SCAN_KEY, String(scannedUserId));
  } catch {
    /* ignore */
  }
}

function clearPendingQrScan(): void {
  try {
    sessionStorage.removeItem(PENDING_QR_SCAN_KEY);
  } catch {
    /* ignore */
  }
}

function getPendingQrScanUserId(): string | null {
  try {
    return sessionStorage.getItem(PENDING_QR_SCAN_KEY);
  } catch {
    return null;
  }
}

/** После регистрации/логина: записать скан с отложенной страницы /scan/:id. */
export async function flushPendingQrScan(): Promise<string | null> {
  const pending = getPendingQrScanUserId();
  if (!pending || !tokenStorage.getAccessToken()) return null;

  const [meRes, scannedRes] = await Promise.all([getCurrentUser(), getUserById(pending)]);
  if (!meRes.data || !scannedRes.data) return pending;

  const me = mapApiProfileToUserProfile(meRes.data);
  const scanned = mapApiProfileToUserProfile(scannedRes.data);
  if (String(me.id) === String(scanned.id)) {
    clearPendingQrScan();
    return null;
  }

  const score = scanned.compatibilityScore ?? 0;
  const saved = await saveScanEvent({
    scannedUserId: String(scanned.id),
    scannerUserId: String(me.id),
    scannerProfile: me,
    compatibility: score,
    viewed: false,
  });
  if (saved) clearPendingQrScan();
  return saved ? pending : null;
}

export async function saveScanEvent(
  event: Omit<ScanEvent, "id" | "timestamp">,
): Promise<ScanEvent | null> {
  if (!tokenStorage.getAccessToken()) return null;
  const res = await recordQrScan(event.scannedUserId, event.compatibility);
  if (!res.data || res.error) return null;
  return mapApiScan(res.data);
}

export async function getUnviewedScanEvents(_userId: string | number): Promise<ScanEvent[]> {
  if (!tokenStorage.getAccessToken()) return [];
  const res = await fetchUnviewedQrScans();
  if (!res.data || res.error) return [];
  // Доп.17 #705 (Ирина 20:34): один скан — ОДНА карточка. Стефа сканировала QR
  // один раз, а в уведомлениях оказалось четыре записи — дедупликация по
  // сканировавшему: оставляем самую свежую запись на каждого scannerUserId.
  const byScanner = new Map<string, ScanEvent>();
  for (const scan of res.data.map(mapApiScan)) {
    const existing = byScanner.get(scan.scannerUserId);
    if (!existing || scan.timestamp > existing.timestamp) byScanner.set(scan.scannerUserId, scan);
  }
  return [...byScanner.values()].sort((a, b) => b.timestamp - a.timestamp);
}

export async function markScanEventAsViewed(eventId: string): Promise<void> {
  if (!tokenStorage.getAccessToken()) return;
  await markQrScanViewed(eventId);
}

/** История сканов (#779): ВСЕ сканы меня, включая просмотренные.
 * Дедупликация по сканировавшему — одна карточка на пользователя,
 * дата/место — самые свежие (повторный скан обновляет строку на бэке). */
export async function getScanHistory(_userId: string | number): Promise<ScanEvent[]> {
  if (!tokenStorage.getAccessToken()) return [];
  const res = await fetchQrScanHistory();
  if (!res.data || res.error) return [];
  const byScanner = new Map<string, ScanEvent>();
  for (const scan of res.data.map(mapApiScan)) {
    const existing = byScanner.get(scan.scannerUserId);
    if (!existing || scan.timestamp > existing.timestamp) byScanner.set(scan.scannerUserId, scan);
  }
  return [...byScanner.values()].sort((a, b) => b.timestamp - a.timestamp);
}

export function cleanupOldScanEvents(): void {
  /* server-side retention; noop on web */
}
